/* eslint-disable react-refresh/only-export-components */
import { createContext, useContext, useEffect, useRef, useState } from 'react'
import type { QueryClient } from '@tanstack/react-query'
import { usePostHog } from '@posthog/react'
import type { User } from '@supabase/supabase-js'
import { clearSentryUser, setSentryUser } from '@/lib/sentry'
import { supabase } from '@/lib/supabase/client'

export interface AuthContext {
  user: User | null
  isAuthenticated: boolean
  isLoading: boolean
}

const AuthContext = createContext<AuthContext | null>(null)

interface AuthProviderProps {
  children: React.ReactNode
  queryClient: QueryClient
}

export function AuthProvider({ children, queryClient }: AuthProviderProps) {
  const [user, setUser] = useState<User | null>(null)
  const [authInitialized, setAuthInitialized] = useState(false)
  // Loading: waiting for auth initialization OR profile (when authenticated)
  const isLoading = !authInitialized
  const authInitializedRef = useRef(false)
  const posthog = usePostHog()

  useEffect(() => {
    // Get initial session
    supabase.auth.getSession().then(({ data: { session } }) => {
      setUser(session?.user ?? null)
      setAuthInitialized(true)
      authInitializedRef.current = true

      // Set Sentry + PostHog user context for initial session
      if (session?.user) {
        setSentryUser({
          id: session.user.id,
          email: session.user.email,
        })
        posthog?.identify(session.user.id, {
          email: session.user.email,
        })
      } else {
        clearSentryUser()
      }
    })

    // Listen for auth state changes
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, session) => {
      setUser(session?.user ?? null)

      // Update Sentry + PostHog user context when auth state changes
      if (session?.user) {
        setSentryUser({
          id: session.user.id,
          email: session.user.email,
        })
        posthog?.identify(session.user.id, {
          email: session.user.email,
        })
      } else {
        clearSentryUser()
      }

      if (event === 'SIGNED_OUT' && authInitializedRef.current) {
        posthog?.reset()
        queryClient.clear()
        window.location.href = '/sign-in'
      }
    })

    return () => subscription.unsubscribe()
  }, [queryClient, posthog])

  const auth: AuthContext = {
    user,
    isAuthenticated: !!user,
    isLoading,
  }

  return <AuthContext.Provider value={auth}>{children}</AuthContext.Provider>
}

/**
 * Hook to access authentication state.
 * Returns nullable user and profile - suitable for all contexts.
 */
export function useAuth(): AuthContext {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}
